"use client";

import type { Alignment } from "@/app/_api/generateTts";
import type { TextEditorRef } from "@ui/components/core/TextEditor/types";

import TextEditor from "@ui/components/core/TextEditor";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TextHighlighter } from "../TextHighlighter";
import { CHARACTER_LIMIT } from "../constants";

const CharacterCounter = ({
  text,
  className,
  maxCharacters,
}: {
  className?: string;
  maxCharacters: number;
  text: string;
}) => {
  const isNearLimit = text.length > maxCharacters * 0.8;

  return (
    <div
      className={cn(
        "pointer-events-none absolute bottom-4 right-16 z-1 flex items-center justify-center rounded-full px-8 py-2 shadow-xs transition-opacity duration-200",
        isNearLimit ? "bg-negative opacity-100" : "opacity-0",
        text.length > maxCharacters && "bg-red text-inverse",
        className,
      )}
    >
      <span className='f-ui-05'>
        {text.length}/{maxCharacters}
      </span>
    </div>
  );
};

export const TextArea = forwardRef<
  TextEditorRef,
  {
    className?: string;
    maxCharacters: number;
    onTextChange: (text: string) => void;
    placeholder?: string;
    text: string;
    withBrackets: boolean;
  }
>(({ className, text, onTextChange, maxCharacters, withBrackets, placeholder }, ref) => {
  const t = useTranslations("ClientKeys.TTS");
  return (
    <div className='relative h-full min-h-0'>
      <div className={cn("relative h-full w-full flex-1 bg-none px-16 py-16 lg:px-24", className)}>
        <TextEditor
          className='flex-1 resize-none outline-none'
          onChange={onTextChange}
          placeholder={placeholder || t("widget.placeholder")}
          ref={ref}
          value={text}
          withBrackets={withBrackets}
        />
      </div>
      <CharacterCounter maxCharacters={maxCharacters} text={text} />
    </div>
  );
});

TextArea.displayName = "TextArea";

export const TextAreaWithHighlighter = ({
  alignment,
  audioElementRef,
  onTextChange,
  text,
  playingState,
  withBrackets,
  isStreaming,
  className,
  autoFocus,
  placeholder,
}: {
  alignment: Alignment | null;
  audioElementRef: React.RefObject<HTMLAudioElement | null>;
  autoFocus?: boolean;
  className?: string;
  isStreaming: boolean;
  onTextChange: (text: string) => void;
  placeholder?: string;
  playingState: string;
  text: string;
  withBrackets: boolean;
}) => {
  const [showHighlighter, setShowHighlighter] = useState(false);
  const textAreaRef = useRef<TextEditorRef>(null);
  const textHighlighterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        textAreaRef.current?.focus();
        textAreaRef.current?.setCursorPosition(text.length + 1);
      }, 500);
    }
  }, [autoFocus]);

  const displayCharacters = useMemo(() => {
    return text.split("");
  }, [text]);

  const handleHighlighterClick = useCallback(
    (characterIndex: number) => {
      // Capture the scroll position from the highlighter before hiding it
      const highlighterScrollTop = textHighlighterRef.current?.scrollTop || 0;

      setShowHighlighter(false);

      // Use setTimeout to ensure the focus happens after the state update and re-render
      setTimeout(() => {
        if (textAreaRef.current) {
          const textArea = textAreaRef.current;
          const position = Math.min(characterIndex + 1, text.length);

          // First focus the textarea
          textArea.focus();

          // Set the scroll position to match the highlighter's position
          textArea.setScrollTop(highlighterScrollTop);

          // Set the selection range
          textArea.setCursorPosition(position);
        }
      }, 0);
    },
    [text.length],
  );

  useEffect(() => {
    if (playingState === "playing") {
      setShowHighlighter(true);
    }
  }, [playingState]);

  useEffect(() => {
    setShowHighlighter(false);
  }, [text]);

  return (
    <>
      {showHighlighter && (
        <TextHighlighter
          characters={alignment?.characters ?? []}
          className={cn("scrollbar-none flex-1 overflow-y-auto p-16 pb-0 lg:px-24", className)}
          displayCharacters={isStreaming ? displayCharacters : undefined}
          endTimes={alignment?.character_end_times_seconds ?? []}
          mediaElement={audioElementRef.current}
          onClick={handleHighlighterClick}
          ref={textHighlighterRef}
          startTimes={alignment?.character_start_times_seconds ?? []}
          withBrackets={withBrackets}
        />
      )}
      {!showHighlighter && (
        <TextArea
          className={cn("overflow-y-auto p-16 pb-0 lg:px-24", showHighlighter && "hidden", className)}
          maxCharacters={CHARACTER_LIMIT}
          onTextChange={onTextChange}
          placeholder={placeholder}
          ref={textAreaRef}
          text={text}
          withBrackets={withBrackets}
        />
      )}
    </>
  );
};
